import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeftIcon } from 'lucide-react';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { tenantsApi } from '@/lib/api/tenants';
import { extensionsApi } from '@/lib/api/extensions';
import { resolveExtensionComponent } from '@/extensions/registry';
import type { Extension, Tenant } from '@/types/entities';

export function AdminTenantExtensionPage() {
  const navigate = useNavigate();
  const { slug, extensionName } = useParams<{ slug: string; extensionName: string }>();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [extension, setExtension] = useState<Extension | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!slug || !extensionName) {
        setError('Invalid tenant or extension path.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const [tenants, tenantExtensions] = await Promise.all([
          tenantsApi.getAll(),
          extensionsApi.getAllForTenant(slug),
        ]);

        const foundTenant = tenants.find((item) => item.slug === slug) ?? null;
        if (!foundTenant) {
          setError('Tenant not found.');
          setTenant(null);
          setExtension(null);
          return;
        }

        const foundExtension = tenantExtensions.find(
          (item) => item.name === extensionName && item.enabled && item.type === 'cms'
        ) ?? null;

        if (!foundExtension) {
          setError('Extension not found or disabled for this tenant.');
          setTenant(foundTenant);
          setExtension(null);
          return;
        }

        setTenant(foundTenant);
        setExtension(foundExtension);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load extension.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [extensionName, slug]);

  const ExtensionComponent = useMemo(() => {
    if (!slug || !extensionName) {
      return null;
    }
    return resolveExtensionComponent(slug, extensionName);
  }, [extensionName, slug]);

  const backHref = slug ? `/admin/dashboard/tenants/${slug}?tab=extensions` : '/admin/dashboard/tenants';

  return (
    <>
      <PageHeader
        title={extension?.display_name || extension?.name || 'Extension'}
        description={tenant ? `Tenant: ${tenant.name}` : 'Tenant extension view'}
        actions={
          <Button variant="outline" onClick={() => navigate(backHref)}>
            <ArrowLeftIcon className="size-4 mr-2" />
            Back to Extensions
          </Button>
        }
      />

      <PageSection>
        {isLoading ? (
          <Card className="p-4">
            <div className="text-muted-foreground">Loading extension...</div>
          </Card>
        ) : null}

        {!isLoading && error ? (
          <Card className="p-4">
            <div className="text-destructive">{error}</div>
          </Card>
        ) : null}

        {!isLoading && !error && !ExtensionComponent ? (
          <Card className="p-4">
            <div className="text-muted-foreground">No component mapping found for this extension.</div>
          </Card>
        ) : null}

        {!isLoading && !error && ExtensionComponent && extension && slug ? (
          <ExtensionComponent tenantSlug={slug} extension={extension} />
        ) : null}
      </PageSection>
    </>
  );
}
